import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useNestedNavigation } from '../../../../../utils/appNavigationNames';
import useOneUpHealthEndpointsSearch from '../../../../../hooks/api/useOneUpEndpointsSearch';
import EndpointCard from '../../../../shared/EndpointCard/EndpointCard';
import AddPortalInstructions from './AddPortalInstructions';
import useOneUpHealthConnect from '../../../../../hooks/api/useOneUpHealthConnect';
import { openURL } from 'expo-linking';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import NoResultsDisplay from './NoResultsDisplay';
import { getIntl } from '../../../../../utils/intl';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { useBuildOneUpHealthConnectUrl } from '../../../../../hooks/useBuildOneUpHealthConnectUrl';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import SearchInput from '../../../../v2BaseComponents/SearchInput';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import MainContentContainer, { mainContentPadding } from '../../../../v2BaseComponents/MainContentContainer';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { PromptActions, StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import { ContentStack, contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import useNotify from '../../../../../hooks/useNotify';
import { supportEmail } from '../../../../../utils/constants';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { usePromptButton } from '../../../../shared/PromptButton';
import PortalConnectionConfirmationContent from './PortalConnectionConfirmationContent';
import { View } from 'react-native';
import { PRFlatList, PRScrollView } from '../../../../shared/Scroll';

const intl = getIntl('profile-add-patient-portal', {
    addPatientPortal: 'Add Patient Portal',
    addManually: 'Add Manually',
    subscriptionRequired: 'Subscription Required',
    subscriptionRequiredMessage: 'Subscription is required to connect to a patient portal.',
    subscribe: 'Subscribe',
    searchPlaceholder: 'Search Health Systems',
    maybeLater: 'Maybe Later',
    unableToOpenEmailApp: 'Unable to open email app',
    emailSupport: 'Email Support',
    unableToFindPortal: 'Unable to find your Portal?',
    unableToFindPortalMessage:
        'We currently have connections to neary 8,000 health systems. If you are unable to find your Portal, please email us at {email}. We will do our best to help!',
    subject: 'Add Portal Request',
    connectToPortal: 'Connect to Portal?',
});

export type AddPortalScreenOwnProps = {};

export type AddPortalScreenProps = AddPortalScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'add-portal'>;
};

const AddPortalScreen: React.FC<AddPortalScreenProps> = (props) => {
    const notify = useNotify();
    const { nestedAccountNavigation } = useNestedNavigation();
    const [showSubscriptionDrawer, setShowSubscriptionDrawer] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const recordEvent = useRecordEvent();
    useSetHeaderTitle(intl('addPatientPortal'));
    const { isLoading, endpoints } = useOneUpHealthEndpointsSearch({ search: searchText });
    const { buildOneUpHealthConnectUrl } = useBuildOneUpHealthConnectUrl();
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const [pendingConnectionEndpointId, setPendingConnectionEndpointId] = React.useState<number | null>(null);
    const [isPendingConnectionPromptOpen, setIsPendingConnectionPromptOpen] = React.useState(false);

    const { connect, isConnecting } = useOneUpHealthConnect({
        medicalProfileId: props.route.params.profileId,
        onConnectUrlReceived: (url) => {
            setPreRedirectArgs(
                {
                    type: 'portal-connect',
                    profileId: props.route.params.profileId,
                    endpointId: pendingConnectionEndpointId,
                },
                () => {
                    openURL(buildOneUpHealthConnectUrl(url));
                },
            );
        },
    });
    const unableToFindPortalPrompt = usePromptButton({
        promptTitle: intl('unableToFindPortal'),
        renderPromptContents: () => (
            <ContentStack>
                <Text variant="body-1">{intl('unableToFindPortalMessage', { email: supportEmail })}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('emailSupport'),
                            onPress: () => {
                                openURL(`mailto:${encodeURIComponent(supportEmail)}?subject=${intl('subject')}`).catch(
                                    () => {
                                        notify('error', intl('unableToOpenEmailApp'));
                                    },
                                );
                            },
                            icon: 'Send',
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    const renderLoadedContent = () => {
        if (!searchText) {
            return (
                <PRScrollView
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{
                        padding: mainContentPadding,
                    }}
                >
                    <AddPortalInstructions />
                </PRScrollView>
            );
        } else if (!endpoints.length) {
            return (
                <PRScrollView
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{
                        padding: mainContentPadding,
                    }}
                >
                    <NoResultsDisplay profileId={props.route.params.profileId} />
                </PRScrollView>
            );
        } else {
            return (
                <PRFlatList
                    keyExtractor={(_) => String(_.id)}
                    renderItem={({ item }) => (
                        <EndpointCard
                            isDisabled={isConnecting}
                            endpoint={{ ...item, isDisabled: false }}
                            key={item.id}
                            onPress={() => {
                                recordEvent('portalSearch', {
                                    portalSearchTerm: searchText,
                                });
                                setIsPendingConnectionPromptOpen(true);
                                setPendingConnectionEndpointId(item.id);
                            }}
                        />
                    )}
                    data={endpoints}
                    style={{ flex: 1, width: '100%' }}
                    contentContainerStyle={{ padding: mainContentPadding }}
                    ItemSeparatorComponent={() => <View style={{ height: contentStackSpacing }} />}
                />
            );
        }
    };

    return (
        <>
            <ProfileHeaderContainer profileId={props.route.params.profileId} omitChatButton>
                <ActionButtonLayout
                    actions={[
                        {
                            title: intl('unableToFindPortal'),
                            onPress: unableToFindPortalPrompt.onPress,
                            show: !!searchText && !isLoading,
                            icon: 'HelpCircle',
                        },
                    ]}
                >
                    <HeaderContainer>
                        <SearchInput
                            testID="portal-search-input"
                            value={searchText}
                            onChange={setSearchText}
                            debounceInterval={250}
                            isDisabled={isConnecting}
                            placeholder={intl('searchPlaceholder')}
                        />
                    </HeaderContainer>
                    <AnimateLoadedContent isLoading={isLoading} style={{ flex: 1, width: '100%' }}>
                        {renderLoadedContent}
                    </AnimateLoadedContent>
                    <StandardPrompt
                        isOpen={showSubscriptionDrawer}
                        setIsOpen={setShowSubscriptionDrawer}
                        title={intl('subscriptionRequired')}
                    >
                        <MainContentContainer>
                            <ContentStack>
                                <Text variant="body-1">{intl('subscriptionRequiredMessage')}</Text>
                                <PromptActions
                                    actions={[
                                        {
                                            title: 'subscribe',
                                            onPress: () => {
                                                setShowSubscriptionDrawer(false);
                                                nestedAccountNavigation('subscription', undefined);
                                            },
                                            icon: 'CreditCard',
                                        },
                                        {
                                            title: intl('maybeLater'),
                                            onPress: () => setShowSubscriptionDrawer(false),
                                            variant: 'secondary',
                                            icon: 'X',
                                        },
                                    ]}
                                />
                            </ContentStack>
                        </MainContentContainer>
                    </StandardPrompt>
                    <StandardPrompt
                        isOpen={isPendingConnectionPromptOpen}
                        setIsOpen={setIsPendingConnectionPromptOpen}
                        title={intl('connectToPortal')}
                    >
                        {pendingConnectionEndpointId ? (
                            <PortalConnectionConfirmationContent
                                endpointId={pendingConnectionEndpointId}
                                onConnect={() => {
                                    recordEvent('connectPatientPortal');
                                    connect({
                                        healthSystemId: pendingConnectionEndpointId,
                                    });
                                }}
                                onCancel={() => {
                                    setIsPendingConnectionPromptOpen(false);
                                }}
                                isConnecting={isConnecting}
                            />
                        ) : null}
                    </StandardPrompt>
                </ActionButtonLayout>
            </ProfileHeaderContainer>
            {unableToFindPortalPrompt.Prompt}
        </>
    );
};

export default React.memo(AddPortalScreen);
